import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import NavbarPage from "../../components/Navbar/Navbar_Page";
import Social from "../../components/Social/Social";
import Footer from "../../components/Footer/footer";

import "../Index10/Index10.css";
import "./Features.css";

/* ================================================================
   FEATURE SHOWCASE HARNESS
   Each entry below renders a full marketing section (copy + visual).
   To showcase a new feature area: add an object to SECTIONS and,
   if it needs a bespoke visual, register a component in VISUALS.
   ================================================================ */

const SECTIONS = [
  {
    id: "collect",
    label: "Collect",
    title: ["Feedback in, from ", "every channel"],
    desc: "Bugs, feature requests, ideas, and questions — captured on your feedback portal, inside your own product, or straight from the places your customers already are.",
    visual: "collect",
    points: [
      {
        icon: "pe-7s-note2",
        title: "Structured submissions",
        desc: "Ticket types, priorities, file attachments, custom fields, and submission templates keep every report actionable.",
      },
      {
        icon: "pe-7s-browser",
        title: "Widget & browser extension",
        desc: "Embed a feedback form, backlog, roadmap, or changelog on any site — or capture feedback with the browser extension.",
      },
      {
        icon: "pe-7s-star",
        title: "Review aggregation",
        desc: "Auto-import App Store and Trustpilot reviews as tickets, deduplicated and ready to triage.",
      },
      {
        icon: "pe-7s-copy-file",
        title: "Duplicate detection",
        desc: "Similar posts surface during submission, so feedback consolidates instead of fragmenting.",
      },
      {
        icon: "pe-7s-download",
        title: "Import from Canny or CSV",
        desc: "Bring an existing board across with its posts, votes, comments, images, and changelog — voters matched by email.",
      },
    ],
  },
  {
    id: "manage",
    label: "Manage",
    title: ["A kanban board that ", "pulls its weight"],
    desc: "Not a toy board. Sprints, WIP limits, swimlanes, and flow analytics — real project-management muscle, attached directly to your customer feedback.",
    visual: "kanban",
    points: [
      {
        icon: "pe-7s-timer",
        title: "Sprints & burndown",
        desc: "Plan iterations, track scope, and watch the burndown update as cards move.",
      },
      {
        icon: "pe-7s-attention",
        title: "WIP limits & aging",
        desc: "Column limits flag overload; aging indicators call out cards going stale.",
      },
      {
        icon: "pe-7s-menu",
        title: "Swimlanes & filters",
        desc: "Slice the board by assignee, priority, or type — save the views your team actually uses.",
      },
      {
        icon: "pe-7s-check",
        title: "Checklists & quick actions",
        desc: "Break tickets into steps and update them without ever opening a card.",
      },
      {
        icon: "pe-7s-users",
        title: "Live presence",
        desc: "See teammates on the board and watch cards move the moment they're dragged.",
      },
      {
        icon: "pe-7s-graph1",
        title: "Flow metrics & CFD",
        desc: "Cycle time, throughput, and cumulative flow diagrams built right in.",
      },
    ],
  },
  {
    id: "ai",
    label: "AI",
    title: ["AI that keeps your backlog ", "honest"],
    desc: "Every submission is scored the moment it arrives, so you spend triage time on feedback that's real, relevant, and new.",
    visual: "ai",
    points: [
      {
        icon: "pe-7s-shield",
        title: "AI content detection",
        desc: "Behavioral and text analysis flags bot-written and spammy submissions before they clutter the board.",
      },
      {
        icon: "pe-7s-target",
        title: "Relevancy scoring",
        desc: "AI rates how relevant each submission is to your product, so the noise sorts itself to the bottom.",
      },
      {
        icon: "pe-7s-repeat",
        title: "Duplicate suggestions",
        desc: "Similar tickets are surfaced automatically for one-click merging.",
      },
      {
        icon: "pe-7s-magic-wand",
        title: "AI-drafted changelog",
        desc: "Turn a sprint's Done column into a polished changelog draft in one click.",
      },
      {
        icon: "pe-7s-filter",
        title: "Spam auto-hold",
        desc: "Link-drops, promo junk, and contact bait score as spam and wait in a review queue instead of going live.",
      },
      {
        icon: "pe-7s-micro",
        title: "Transcript import",
        desc: "Paste a call transcript and AI drafts the tickets — with speaker attribution and quotes. You approve each one.",
      },
    ],
  },
  {
    id: "roadmap",
    label: "Close the loop",
    title: ["Roadmaps & changelogs ", "worth following"],
    desc: "Show customers what's planned, what's in flight, and what just shipped — and automatically tell the people who asked for it.",
    visual: "changelog",
    points: [
      {
        icon: "pe-7s-map-2",
        title: "Public roadmaps",
        desc: "Custom columns, voting, and filtering. Embed the roadmap on your own site with one script tag.",
      },
      {
        icon: "pe-7s-news-paper",
        title: "Changelog",
        desc: "Labeled entries linked back to the tickets they resolve — proof you ship what people ask for.",
      },
      {
        icon: "pe-7s-bell",
        title: "Loop-closing notifications",
        desc: "When a ticket ships, everyone who voted or followed it gets notified automatically.",
      },
      {
        icon: "pe-7s-display1",
        title: "Embeds everywhere",
        desc: "Roadmap, changelog, and feedback widgets drop into any page on your site.",
      },
    ],
  },
  {
    id: "community",
    label: "Community",
    title: ["Let your users do the ", "prioritizing"],
    desc: "Voting, polls, and recognition turn a feedback form into a community that tells you exactly what to build next.",
    visual: "community",
    points: [
      {
        icon: "pe-7s-like",
        title: "Voting",
        desc: "Upvotes surface the highest-impact ideas; vote counts follow tickets everywhere.",
      },
      {
        icon: "pe-7s-graph2",
        title: "Prioritization polls",
        desc: "Put a shortlist in front of your users and let them settle the debate.",
      },
      {
        icon: "pe-7s-medal",
        title: "Badges & contributors",
        desc: "Leaderboards and achievement badges reward your most engaged users.",
      },
      {
        icon: "pe-7s-cash",
        title: "Sponsorships",
        desc: "Let customers put money behind the features they want, powered by Stripe.",
      },
    ],
  },
  {
    id: "revenue",
    label: "Revenue",
    title: ["Know which feedback ", "pays the bills"],
    desc: "Tie requests to the CRM records behind them, weight demand by customer value, and make sure the right account owner delivers the good news when it ships.",
    visual: "revenue",
    points: [
      {
        icon: "pe-7s-link",
        title: "CRM record linking",
        desc: "Attach Salesforce accounts and opportunities or HubSpot companies and deals to any ticket — revenue totals roll up automatically.",
      },
      {
        icon: "pe-7s-plugin",
        title: "Automatic opportunity linking",
        desc: "A new ticket from a customer with exactly one open opportunity links itself. No hunting in the CRM.",
      },
      {
        icon: "pe-7s-graph3",
        title: "Demand by customer value",
        desc: "Rank requests by the revenue behind their voters, not just raw vote counts.",
      },
      {
        icon: "pe-7s-mail",
        title: "Account-owner alerts",
        desc: "When a customer's request ships, the CSM who owns that account hears first — and can tell them personally.",
      },
    ],
  },
  {
    id: "integrations",
    label: "Integrations",
    title: ["Two-way sync with ", "your entire stack"],
    desc: "Twenty integrations, outgoing webhooks, Zapier, Segment, and SDKs in seven languages. Feedback flows to where the work happens — and status flows back.",
    visual: "integrations",
    points: [
      {
        icon: "pe-7s-refresh-2",
        title: "Two-way issue sync",
        desc: "GitHub, Jira, Linear, GitLab, Azure DevOps, ClickUp, Asana, Notion — status and comments stay in step.",
      },
      {
        icon: "pe-7s-headphones",
        title: "Support-desk ready",
        desc: "Intercom, Zendesk, Help Scout, and Freshdesk sidebars link conversations to tickets.",
      },
      {
        icon: "pe-7s-link",
        title: "Webhooks, Zapier & Segment",
        desc: "HMAC-signed webhooks, REST hooks, and analytics events for everything else.",
      },
      {
        icon: "pe-7s-tools",
        title: "API, SDKs & MCP",
        desc: "A full REST API, SDKs in seven languages, and an MCP server so AI assistants like Claude and ChatGPT can work your backlog.",
      },
    ],
  },
  {
    id: "platform",
    label: "Platform",
    title: ["Ready for ", "serious teams"],
    desc: "Enterprise sign-in, private backlogs, granular notifications, and white-labeling — without the enterprise sales call.",
    visual: "platform",
    points: [
      {
        icon: "pe-7s-key",
        title: "Single sign-on",
        desc: "Generic OIDC works with Okta, Entra ID, Auth0, and Google Workspace. Auto-provisioning included.",
      },
      {
        icon: "pe-7s-lock",
        title: "Private backlogs",
        desc: "Invite-only boards for internal teams, beta groups, or key accounts.",
      },
      {
        icon: "pe-7s-bell",
        title: "Notification control",
        desc: "A full preference matrix, email digests, and real-time in-app updates.",
      },
      {
        icon: "pe-7s-paint-bucket",
        title: "White-label",
        desc: "Remove PublicBacklog branding from widgets and embeds on higher tiers.",
      },
      {
        icon: "pe-7s-comment",
        title: "Comment moderation",
        desc: "Hold public comments until a moderator approves them — notifications and syncs wait too.",
      },
      {
        icon: "pe-7s-mail-open-file",
        title: "Custom email templates",
        desc: "Edit the subject and body of the emails sent on your behalf, per organization.",
      },
    ],
  },
];

const INTEGRATIONS = [
  { name: "Slack", color: "#4A154B" },
  { name: "Jira", color: "#0052CC" },
  { name: "GitHub", color: "#6e7681" },
  { name: "GitLab", color: "#FC6D26" },
  { name: "Linear", color: "#5E6AD2" },
  { name: "Asana", color: "#F06A6A" },
  { name: "Intercom", color: "#1F8DED" },
  { name: "Zendesk", color: "#03363D" },
  { name: "Salesforce", color: "#00A1E0" },
  { name: "HubSpot", color: "#FF7A59" },
  { name: "Notion", color: "#999" },
  { name: "Teams", color: "#6264A7" },
  { name: "Azure DevOps", color: "#0078D7" },
  { name: "ClickUp", color: "#7B68EE" },
  { name: "Help Scout", color: "#1292EE" },
  { name: "Freshdesk", color: "#25C16F" },
  { name: "Zapier", color: "#FF4A00" },
  { name: "Segment", color: "#52BD94" },
  { name: "Webhooks", color: "#4f46e5" },
  { name: "Discord", color: "#5865F2" },
];

/* ---------- Visual mockups (pure CSS/JSX, no screenshots) ---------- */

const CardHeader = ({ label }) => (
  <div className="ht-dashboard-header">
    <span className="ht-dashboard-dot" style={{ background: "#ff5f57" }} />
    <span className="ht-dashboard-dot" style={{ background: "#febc2e" }} />
    <span className="ht-dashboard-dot" style={{ background: "#28c840" }} />
    <span className="fp-card-label">{label}</span>
  </div>
);

const CollectVisual = () => (
  <div className="fp-visual-card">
    <CardHeader label="Submit feedback" />
    <div className="fp-visual-body">
      <div className="fp-form-label">Type</div>
      <div className="fp-type-pills">
        <span className="fp-pill fp-pill-bug">Bug</span>
        <span className="fp-pill fp-pill-active">Request</span>
        <span className="fp-pill">Idea</span>
        <span className="fp-pill">Question</span>
      </div>
      <div className="fp-form-label">Title</div>
      <div className="fp-input">Bulk edit for tickets</div>
      <div className="fp-form-label">Details</div>
      <div className="fp-textarea">
        <span className="fp-text-line" style={{ width: "92%" }} />
        <span className="fp-text-line" style={{ width: "70%" }} />
      </div>
      <div className="fp-attach">
        <i className="pe-7s-paperclip" /> screenshot.png
      </div>
      <div className="fp-dup-hint">
        <i className="pe-7s-copy-file" />
        Similar: “Bulk actions on kanban” — 32 votes
      </div>
      <div className="fp-submit-btn">Submit feedback</div>
    </div>
  </div>
);

const KanbanVisual = () => (
  <div className="fp-visual-card">
    <CardHeader label="Kanban" />
    <div className="fp-visual-body">
      <div className="fp-sprint-bar">
        <span className="fp-sprint-name">Sprint 14 · 6 days left</span>
        <span className="fp-burndown">
          {[90, 78, 70, 55, 48, 32, 20].map((h, i) => (
            <span key={i} className="fp-burndown-bar" style={{ height: `${h}%` }} />
          ))}
        </span>
      </div>
      <div className="ht-mini-kanban">
        <div className="ht-kanban-col">
          <div className="ht-kanban-col-title" style={{ borderColor: "#4f46e5" }}>
            New <span className="fp-wip">4</span>
          </div>
          <div className="ht-kanban-card">
            <div className="ht-kanban-card-title">Dark mode support</div>
            <div className="ht-kanban-card-meta">
              <span className="fp-tag fp-tag-indigo">Request</span>
              <span className="ht-kanban-votes">&uarr; 24</span>
            </div>
          </div>
          <div className="ht-kanban-card fp-card-aging">
            <div className="ht-kanban-card-title">CSV export broken</div>
            <div className="ht-kanban-card-meta">
              <span className="fp-tag fp-tag-red">Bug</span>
              <span className="fp-aging">12d</span>
            </div>
          </div>
        </div>
        <div className="ht-kanban-col">
          <div className="ht-kanban-col-title" style={{ borderColor: "#6366f1" }}>
            In Progress <span className="fp-wip fp-wip-hot">3/3</span>
          </div>
          <div className="ht-kanban-card">
            <div className="ht-kanban-card-title">Slack integration</div>
            <div className="fp-checklist">
              <span className="fp-checklist-fill" style={{ width: "60%" }} />
            </div>
            <div className="ht-kanban-card-meta">
              <span className="fp-checklist-count">3/5</span>
              <span className="fp-avatars">
                <span className="fp-avatar" style={{ background: "#4f46e5" }}>A</span>
                <span className="fp-avatar" style={{ background: "#818cf8" }}>M</span>
              </span>
            </div>
          </div>
          <div className="ht-kanban-card fp-card-blocked">
            <div className="ht-kanban-card-title">Webhook retries</div>
            <div className="ht-kanban-card-meta">
              <span className="fp-tag fp-tag-red">Blocked</span>
            </div>
          </div>
        </div>
        <div className="ht-kanban-col">
          <div className="ht-kanban-col-title" style={{ borderColor: "#818cf8" }}>
            Done
          </div>
          <div className="ht-kanban-card">
            <div className="ht-kanban-card-title">SSO login</div>
            <div className="ht-kanban-card-meta">
              <span className="fp-tag fp-tag-violet">Idea</span>
              <span className="ht-kanban-votes">&uarr; 67</span>
            </div>
          </div>
        </div>
      </div>
      <div className="fp-presence">
        <span className="fp-presence-dot" /> 2 teammates viewing this board
      </div>
    </div>
  </div>
);

const AiVisual = () => (
  <div className="fp-visual-card">
    <CardHeader label="AI triage" />
    <div className="fp-visual-body">
      <div className="fp-meter-row">
        <span className="fp-meter-label">AI likelihood</span>
        <span className="fp-meter">
          <span className="fp-meter-fill fp-meter-green" style={{ width: "12%" }} />
        </span>
        <span className="fp-meter-value fp-meter-value-green">12 · Human</span>
      </div>
      <div className="fp-meter-row">
        <span className="fp-meter-label">Relevancy</span>
        <span className="fp-meter">
          <span className="fp-meter-fill fp-meter-indigo" style={{ width: "84%" }} />
        </span>
        <span className="fp-meter-value fp-meter-value-indigo">84 · Excellent</span>
      </div>
      <div className="fp-meter-row">
        <span className="fp-meter-label">Spam</span>
        <span className="fp-meter">
          <span className="fp-meter-fill fp-meter-green" style={{ width: "6%" }} />
        </span>
        <span className="fp-meter-value fp-meter-value-green">6 · Clean</span>
      </div>
      <div className="fp-ai-row">
        <i className="pe-7s-repeat" /> 2 possible duplicates found
        <span className="fp-ai-action">Review</span>
      </div>
      <div className="fp-ai-row">
        <i className="pe-7s-micro" /> Transcript scanned — 4 draft tickets
        <span className="fp-ai-action">Review drafts</span>
      </div>
      <div className="fp-ai-row">
        <i className="pe-7s-magic-wand" /> Changelog draft ready — 4 tickets
        <span className="fp-ai-action">Open draft</span>
      </div>
    </div>
  </div>
);

const RevenueVisual = () => (
  <div className="fp-visual-card">
    <CardHeader label="Customer value" />
    <div className="fp-visual-body">
      <div className="fp-form-label">MRR behind this request</div>
      <div className="fp-cl-title" style={{ fontSize: 28, marginBottom: 4 }}>
        $184,000
      </div>
      <div className="fp-form-label" style={{ marginBottom: 12 }}>
        12 upvoters · 3 linked CRM records
      </div>
      <div className="fp-lb" style={{ marginTop: 0 }}>
        <div className="fp-lb-title">Top customers asking</div>
        <div className="fp-lb-row">
          <span className="fp-avatar" style={{ background: "#4f46e5" }}>A</span>
          Acme Corp
          <span className="fp-lb-pts">$96k</span>
        </div>
        <div className="fp-lb-row">
          <span className="fp-avatar" style={{ background: "#818cf8" }}>G</span>
          Globex
          <span className="fp-lb-pts">$54k</span>
        </div>
      </div>
      <div className="fp-ai-row">
        <i className="pe-7s-link" /> Open opportunity auto-linked
        <span className="fp-ai-action">View deal</span>
      </div>
      <div className="fp-cl-foot">
        <i className="pe-7s-mail" /> Account owner notified when this ships
      </div>
    </div>
  </div>
);

const ChangelogVisual = () => (
  <div className="fp-visual-card">
    <CardHeader label="Changelog" />
    <div className="fp-visual-body">
      <div className="fp-cl-labels">
        <span className="fp-tag fp-tag-green">New</span>
        <span className="fp-tag fp-tag-indigo">Improved</span>
        <span className="fp-cl-date">June 2026</span>
      </div>
      <div className="fp-cl-title">Sprints & burndown charts</div>
      <div className="fp-textarea" style={{ marginBottom: 16 }}>
        <span className="fp-text-line" style={{ width: "95%" }} />
        <span className="fp-text-line" style={{ width: "88%" }} />
        <span className="fp-text-line" style={{ width: "55%" }} />
      </div>
      <div className="fp-cl-tickets">
        <span className="fp-cl-ticket">#482 · Sprint planning &uarr; 41</span>
        <span className="fp-cl-ticket">#513 · Burndown view &uarr; 27</span>
      </div>
      <div className="fp-cl-foot">
        <i className="pe-7s-bell" /> 68 voters notified when this shipped
      </div>
    </div>
  </div>
);

const CommunityVisual = () => (
  <div className="fp-visual-card">
    <CardHeader label="Prioritization poll" />
    <div className="fp-visual-body">
      <div className="fp-cl-title" style={{ marginBottom: 16 }}>
        What should we build next?
      </div>
      {[
        { name: "Dark mode", pct: 46 },
        { name: "Mobile app", pct: 31 },
        { name: "API v2", pct: 23 },
      ].map((opt, i) => (
        <div key={i} className="fp-poll-opt">
          <span className="fp-poll-bar">
            <span className="fp-poll-fill" style={{ width: `${opt.pct}%` }} />
            <span className="fp-poll-name">{opt.name}</span>
          </span>
          <span className="fp-poll-pct">{opt.pct}%</span>
        </div>
      ))}
      <div className="fp-lb">
        <div className="fp-lb-title">Top contributors</div>
        <div className="fp-lb-row">
          <span className="fp-avatar" style={{ background: "#4f46e5" }}>A</span>
          Ana K. <span className="fp-tag fp-tag-violet">Bug Hunter</span>
          <span className="fp-lb-pts">128 pts</span>
        </div>
        <div className="fp-lb-row">
          <span className="fp-avatar" style={{ background: "#818cf8" }}>J</span>
          Jon M. <span className="fp-tag fp-tag-indigo">Idea Machine</span>
          <span className="fp-lb-pts">96 pts</span>
        </div>
      </div>
    </div>
  </div>
);

const IntegrationsVisual = () => (
  <div className="fp-visual-card">
    <CardHeader label="Connected tools" />
    <div className="fp-visual-body">
      <div className="fp-pill-grid">
        {INTEGRATIONS.map((intg, i) => (
          <span key={i} className="ht-integration-pill">
            <span className="ht-integration-dot" style={{ background: intg.color }} />
            {intg.name}
          </span>
        ))}
      </div>
    </div>
  </div>
);

const PlatformVisual = () => (
  <div className="fp-visual-card">
    <CardHeader label="Organization security" />
    <div className="fp-visual-body">
      <div className="fp-sec-row">
        <span className="fp-sec-check">✓</span> Single sign-on (OIDC)
        <span className="fp-sec-detail">Okta · Entra ID · Auth0 · Google</span>
      </div>
      <div className="fp-sec-row">
        <span className="fp-sec-check">✓</span> Private backlogs
        <span className="fp-sec-detail">Invite-only</span>
      </div>
      <div className="fp-sec-row">
        <span className="fp-sec-check">✓</span> Roles & permissions
        <span className="fp-sec-detail">Admin / Member</span>
      </div>
      <div className="fp-sec-row">
        <span className="fp-sec-check">✓</span> HMAC-signed webhooks
        <span className="fp-sec-detail">SHA-256</span>
      </div>
      <div className="fp-sec-row">
        <span className="fp-sec-check">✓</span> Encrypted integration credentials
        <span className="fp-sec-detail">At rest</span>
      </div>
      <div className="fp-sec-row">
        <span className="fp-sec-check">✓</span> White-label embeds
        <span className="fp-sec-detail">Higher tiers</span>
      </div>
    </div>
  </div>
);

const VISUALS = {
  collect: <CollectVisual />,
  kanban: <KanbanVisual />,
  ai: <AiVisual />,
  changelog: <ChangelogVisual />,
  community: <CommunityVisual />,
  revenue: <RevenueVisual />,
  integrations: <IntegrationsVisual />,
  platform: <PlatformVisual />,
};

/* ------------------------------- Page ------------------------------- */

const Features = () => {
  const [navItems] = useState([
    { id: 1, link: "/", navheading: "Home" },
    { id: 2, idnm: "collect", navheading: "Collect" },
    { id: 3, idnm: "manage", navheading: "Manage" },
    { id: 4, idnm: "ai", navheading: "AI" },
    { id: 5, idnm: "revenue", navheading: "Revenue" },
    { id: 6, idnm: "integrations", navheading: "Integrations" },
  ]);

  useEffect(() => {
    document.title = "Features — Public Backlog";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="home-ten fp-page">
      {/* Animated background blobs */}
      <div className="ht-mesh-bg">
        <div className="ht-blob ht-blob-1" />
        <div className="ht-blob ht-blob-2" />
        <div className="ht-blob ht-blob-3" />
        <div className="ht-blob ht-blob-4" />
      </div>

      {/* Noise texture overlay */}
      <div className="ht-noise" />

      <div className="ht-content">
        <NavbarPage navItems={navItems} navClass="" />

        {/* Hero */}
        <section className="fp-hero">
          <div className="fp-hero-inner">
            <div className="ht-hero-eyebrow">
              <span className="ht-hero-eyebrow-dot" />
              Product tour
            </div>
            <h1 className="ht-hero-title">
              Everything Public Backlog does,{" "}
              <span className="ht-gradient-text">on one page</span>
            </h1>
            <p className="fp-hero-desc">
              A quick tour of the whole platform — collection, triage, AI,
              roadmaps, community, revenue intelligence, integrations, and
              enterprise controls. Dig deeper anytime in the docs.
            </p>
            <div className="fp-hero-stats">
              <span className="fp-stat-chip"><strong>20</strong> integrations</span>
              <span className="fp-stat-chip"><strong>7</strong> SDK languages</span>
              <span className="fp-stat-chip"><strong>4</strong> embeddable widgets</span>
              <span className="fp-stat-chip"><strong>AI</strong> triage built in</span>
            </div>
            <div className="ht-hero-actions" style={{ justifyContent: "center" }}>
              <a
                href="https://app.publicbacklog.com/user/login"
                className="ht-btn-primary"
              >
                Start free
                <span style={{ fontSize: "1.1em" }}>&rarr;</span>
              </a>
              <a
                href="https://docs.publicbacklog.com"
                target="_blank"
                rel="noopener noreferrer"
                className="ht-btn-secondary"
              >
                Read the docs
              </a>
            </div>
          </div>
        </section>

        {/* Feature showcase sections */}
        {SECTIONS.map((s, i) => (
          <section
            key={s.id}
            id={s.id}
            className={`fp-section${i % 2 === 1 ? " fp-flip" : ""}`}
          >
            <div className="fp-section-inner">
              <div className="fp-copy">
                <div className="ht-section-label">
                  <span
                    style={{
                      width: 16,
                      height: 2,
                      background: "currentColor",
                      display: "inline-block",
                    }}
                  />
                  {s.label}
                </div>
                <h2 className="ht-section-title">
                  {s.title[0]}
                  <span className="ht-gradient-text">{s.title[1]}</span>
                </h2>
                <p className="fp-desc">{s.desc}</p>
                <div className={`fp-points${s.points.length > 4 ? " fp-points-2col" : ""}`}>
                  {s.points.map((p, j) => (
                    <div key={j} className="fp-point">
                      <div className="fp-point-icon">
                        <i className={p.icon} />
                      </div>
                      <div>
                        <div className="fp-point-title">{p.title}</div>
                        <div className="fp-point-desc">{p.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="fp-visual">{VISUALS[s.visual]}</div>
            </div>
          </section>
        ))}

        {/* Big CTA */}
        <section className="ht-cta">
          <div className="ht-cta-glow" />
          <div className="ht-cta-inner">
            <h2 className="ht-cta-title">
              See it with{" "}
              <span className="ht-gradient-text">your own feedback</span>
            </h2>
            <p className="ht-cta-desc">
              Set up a backlog in under two minutes. Free forever on the
              starter plan — no credit card required.
            </p>
            <div className="ht-cta-actions">
              <a
                href="https://app.publicbacklog.com/user/login"
                className="ht-btn-primary"
              >
                Get started for free
                <span style={{ fontSize: "1.1em" }}>&rarr;</span>
              </a>
              <Link to="/" className="ht-btn-secondary">
                Back to home
              </Link>
            </div>
          </div>
        </section>

        <Social />
        <Footer />
      </div>
    </div>
  );
};

export default Features;
